// useAnimation.ts
import { useRef, useCallback, useEffect } from 'react';

export const useAnimation = (callback: () => void, interval: number = 100) => {
  const intervalRef = useRef<number | null>(null);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = window.setInterval(callback, interval);
  }, [callback, interval]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const toggle = useCallback((isRunning: boolean) => {
    if (isRunning) {
      stop();
      return false;
    } else {
      start();
      return true;
    }
  }, [start, stop]);

  // ⬇️ If interval or callback change, retime the existing loop cleanly.
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = window.setInterval(callback, interval);
    }
  }, [callback, interval]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { start, stop, toggle };
};
